<template>
  <div>
    <p v-if="numberOfPetsAtOneTime" class="text-grey-body text-caption mb-8">
      Willing to care for {{ numberOfPetsAtOneTime }} pets per sitting
    </p>

    <div
      v-for="(rate, index) in formattedRates"
      :key="index"
      :class="[
        'flex justify-between items-center',
        careType === 'pet_care' ? 'text-grey-body' : 'text-body',
        rate.isHeader ? 'mt-16' : 'py-8',
      ]"
    >
      <span :class="{ 'text-strong-body': rate.isHeader || rate.label === 'Hourly rate' }">{{ rate.label }}</span>

      <span v-if="!rate.isHeader" class="text-body font-medium">
        <template v-if="isSimpleRate">
          <span class="text-h5">${{ rate.value }}{{ rate.unit }}</span>
        </template>
        <template v-else>
          <span class="caption text-grey-text-subdued align-top">$</span>
          <span class="text-h4">{{ rate.value }}</span>
          <span class="text-body text-grey-text-subdued align-top">/hr</span>
        </template>
      </span>
    </div>

    <div v-if="increasePerChildRate || numberOfChildrenAtOneTime" class="mt-16">
      <p v-if="increasePerChildRate" class="text-body mb-8">Rate per additional child – ${{ increasePerChildRate }}</p>
      <p v-if="numberOfChildrenAtOneTime" class="text-grey-body text-caption">
        Willing to care for {{ numberOfChildrenAtOneTime }} children per sitting
      </p>
    </div>

    <p
      v-if="editModeActive && careType === 'housekeeping' && profile?.distanceWillingToTravel"
      class="flex justify-between text-body"
    >
      <span class="text-strong-body">Distance willing to travel:</span><span>{{ profile.distanceWillingToTravel }} miles</span>
    </p>
    <poppins-button
      v-if="editModeActive && hasIncompleteRates"
      :href="editLink"
      variant="text"
      size="medium"
      value="Add additional rates"
    />
  </div>
</template>

<script lang="ts">
import { PayRate, PetCareRates, CompanionCareRates, ProfileRates } from '../../__types__/RateTypes';
import { isChildCare, isPetCare, isCompanionCare } from '../../__types__/CareTypes';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'ProfileRates',
  components: { PoppinsButton },
  props: {
    careType: { type: String, required: true },
    rates: {
      type: Object as () => {
        payRate: PayRate | null;
        rates: PetCareRates | CompanionCareRates | null;
      },
      required: true,
    },
    profile: {
      type: Object as () => ProfileRates,
      required: true,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    editLink: {
      type: String,
      default: '',
    },
  },
  computed: {
    isSimpleRate(): boolean {
      return isPetCare(this.careType) || isCompanionCare(this.careType);
    },
    numberOfChildrenAtOneTime(): string | number | undefined {
      const value = this.profile.numberOfChildrenAtOneTime;
      return value && value >= 4 ? `${value}+` : value;
    },
    numberOfPetsAtOneTime(): string | number | undefined {
      const val = this.profile.numberOfPetsAtOneTime;
      return val && val >= 4 ? `${val}+` : val;
    },
    increasePerChildRate(): number {
      return this.profile.increasePerChildRate! / 100;
    },
    hasIncompleteRates(): boolean {
      const c = this.careType;
      switch (true) {
        case isChildCare(c):
          return !this.profile.increasePerChildRate;
        case isPetCare(c):
          return this.isMissingPetCareRates;
        case isCompanionCare(c):
          return this.isMissingCompanionCareRates;
        default:
          return false;
      }
    },
    isMissingPetCareRates(): boolean {
      const petRates = this.profile.rates as PetCareRates | null;
      const rateKeys = [
        'visitRate',
        'thirtyMinuteWalkRate',
        'sixtyMinuteWalkRate',
        'dayRate',
        'overnightRateSitterHome',
        'overnightRateOwnerHome',
        'transportationRate',
        'ratePerService',
        'trainingRate',
      ];
      if (!petRates) return true;
      return rateKeys.some((key) => !petRates[key as keyof PetCareRates]);
    },
    isMissingCompanionCareRates(): boolean {
      const companionRates = this.profile.rates as CompanionCareRates | null;
      if (
        !companionRates ||
        !companionRates.homeCareRates ||
        !companionRates.respiteCareRates ||
        !companionRates.transportationRates
      )
        return true;

      const missingHomeCareRates =
        companionRates.homeCareRates &&
        !(
          companionRates.homeCareRates.hourly &&
          companionRates.homeCareRates.halfDay &&
          companionRates.homeCareRates.fullDay &&
          companionRates.homeCareRates.weekly &&
          companionRates.homeCareRates.monthly
        );

      const missingRespiteCareRates =
        companionRates.respiteCareRates &&
        !(
          companionRates.respiteCareRates.hourly &&
          companionRates.respiteCareRates.halfDay &&
          companionRates.respiteCareRates.fullDay
        );

      const missingTransportationRates = companionRates.transportationRates && !companionRates.transportationRates.oneWay;

      return !!missingHomeCareRates || !!missingRespiteCareRates || !!missingTransportationRates;
    },
    formattedRates() {
      const { payRate, rates } = this.rates;
      const isPetCareRates = (r: any): r is PetCareRates => {
        return r && ('visitRate' in r || 'thirtyMinuteWalkRate' in r || 'sixtyMinuteWalkRate' in r || 'dayRate' in r);
      };
      const isCompanionCareRates = (r: any): r is CompanionCareRates => {
        return r && ('homeCareRates' in r || 'respiteCareRates' in r || 'transportationRates' in r);
      };
      const strategies: Record<string, () => any> = {
        babysitter: () => this.formatSimpleRates(payRate),
        nanny: () => this.formatSimpleRates(payRate),
        housekeeping: () => this.formatSimpleRates(payRate),
        pet_care: () => this.formatPetCareRates(isPetCareRates(rates) ? rates : null),
        petCare: () => this.formatPetCareRates(isPetCareRates(rates) ? rates : null),
        companion_care: () => this.formatCompanionCareRates(isCompanionCareRates(rates) ? rates : null),
        companionCare: () => this.formatCompanionCareRates(isCompanionCareRates(rates) ? rates : null),
      };
      if (strategies[this.careType]) return strategies[this.careType]();
      const snake = this.careType.replace(/([A-Z])/g, '_$1').toLowerCase();
      if (strategies[snake]) return strategies[snake]();
      if (isPetCare(this.careType)) return strategies.petCare();
      if (isCompanionCare(this.careType)) return strategies.companionCare();
      return [];
    },
  },
  methods: {
    formatSimpleRates(payRate: PayRate | null) {
      if (!payRate) return [];
      const min = Math.round(payRate.min / 100);
      const max = Math.round(payRate.max / 100);
      const value = min === max ? min : `${min}–${max}`;
      return [{ label: 'Hourly rate', value }];
    },
    formatPetCareRates(petRates: PetCareRates | null) {
      if (!petRates) return [];
      const rateMap = [
        { key: 'visitRate', label: 'Check-ins and visits', unit: ' /visit' },
        { key: 'thirtyMinuteWalkRate', label: '30-minute dog walk', unit: ' /walk' },
        { key: 'sixtyMinuteWalkRate', label: '60-minute dog walk', unit: ' /walk' },
        { key: 'dayRate', label: 'Pet care in my home', unit: ' /day' },
        { key: 'overnightRateSitterHome', label: 'Overnight care (my home)', unit: ' /night' },
        { key: 'overnightRateOwnerHome', label: 'Overnight care (your home)', unit: ' /night' },
        { key: 'transportationRate', label: 'Pick-up and drop-off', unit: ' /way' },
        { key: 'ratePerService', label: 'Bathing and Grooming', unit: ' /service' },
        { key: 'trainingRate', label: 'Training', unit: ' /hour' },
      ];
      return rateMap
        .filter(({ key }) => petRates[key as keyof PetCareRates])
        .map(({ key, label, unit }) => ({
          label,
          value: Math.round((petRates[key as keyof PetCareRates] as number) / 100),
          unit,
        }));
    },
    formatCompanionCareRates(companionRates: CompanionCareRates | null) {
      if (!companionRates) return [];
      const sections = [
        {
          title: 'Home care',
          data: companionRates.homeCareRates,
          fields: [
            { key: 'hourly', label: 'Per hour' },
            { key: 'halfDay', label: 'Per half day' },
            { key: 'fullDay', label: 'Per full day' },
            { key: 'weekly', label: 'Per week' },
            { key: 'monthly', label: 'Per month' },
          ],
        },
        {
          title: 'Respite care',
          data: companionRates.respiteCareRates,
          fields: [
            { key: 'hourly', label: 'Per hour' },
            { key: 'halfDay', label: 'Per half day' },
            { key: 'fullDay', label: 'Per full day' },
          ],
        },
        {
          title: 'Transportation service',
          data: companionRates.transportationRates,
          fields: [{ key: 'oneWay', label: 'One way' }],
        },
      ];
      const rates: any[] = [];
      sections.forEach(({ title, data, fields }) => {
        if (data && fields.some(({ key }) => data[key])) {
          rates.push({ label: title, value: '', isHeader: true });
          fields.forEach(({ key, label }) => {
            if (data[key]) {
              rates.push({ label, value: Math.round(data[key] / 100) });
            }
          });
        }
      });
      return rates;
    },
  },
};
</script>
